import { Flex, Typography } from "antd"
import dayjs from "dayjs"
import React from "react"
import { useTranslation } from "react-i18next"

import { homeColor, homeText } from "../home-theme.ts"
import { type UserInfo } from "../../LoginPage/types/auth.types.ts"
import {
  DashboardHeaderSearch,
  type DashboardTableFilterKey
} from "./DashboardHeaderSearch.tsx"
import { GREETING_DEFAULTS, greetingBandForHour } from "./homeGreeting.ts"

const { Text } = Typography

interface DashboardHeaderProps {
  user: UserInfo | null
  canSearch: boolean
  canCreate: boolean
  tableFilterKey: DashboardTableFilterKey | null
  /** My View / Edit Layout, which join the one toolbar. */
  viewControls?: React.ReactNode
  /** Export, at the toolbar's end. */
  exportControl?: React.ReactNode
}

/**
 * A title block, then one toolbar under it.
 *
 * Greeting and controls used to share a row, which left My View and Edit
 * Layout competing with the welcome line for the eye and squeezed every
 * control narrower as the greeting grew. Two rows give the toolbar the whole
 * width and one consistent 36px control height. The filters keep their own
 * row below, drawn by `FilterBar`.
 */
export function DashboardHeader({
  user,
  canCreate,
  canSearch,
  tableFilterKey,
  viewControls,
  exportControl
}: DashboardHeaderProps) {
  const { t } = useTranslation()
  const name = user?.first_name
  // `dayjs` carries the account's timezone via `DayjsProvider`, so this is
  // the user's own hour rather than the browser's or the server's.
  const band = greetingBandForHour(dayjs().hour())

  return (
    <Flex vertical gap={14}>
      <div className={"min-w-0"}>
        <h1
          className="m-0 text-start"
          style={{ ...homeText.welcomeTitle, color: homeColor.ink }}
        >
          {name
            ? t(
                `dashboard.greeting.${band}`,
                `${GREETING_DEFAULTS[band]}, {{name}}`,
                {
                  name
                }
              )
            : t(`dashboard.greetingNoName.${band}`, GREETING_DEFAULTS[band])}
        </h1>
        <Text
          style={{ ...homeText.welcomeSubtitle, color: homeColor.inkMuted }}
        >
          {t(
            "dashboard.subtitle",
            "Your projects, priorities and business at a glance."
          )}
        </Text>
      </div>

      {/* Customisation on the left, the record actions on the right. They wrap
          as two groups rather than one long run, so a narrow window never
          leaves Export sitting alone under My View. */}
      <Flex
        gap={16}
        wrap="wrap"
        align="center"
        justify="space-between"
        className={"w-full min-w-0"}
      >
        <Flex gap={8} wrap="wrap" align="center">
          {viewControls}
        </Flex>
        <Flex gap={8} wrap="wrap" align="center" className={"max-lg:w-full"}>
          {(canSearch || canCreate) && tableFilterKey && (
            <DashboardHeaderSearch
              canCreate={canCreate}
              tableFilterKey={tableFilterKey}
            />
          )}
          {exportControl}
        </Flex>
      </Flex>
    </Flex>
  )
}
